import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useStytchWorkspaces } from '@/hooks/useStytchWorkspaces'
import { useAppDispatch } from '@/redux/hooks/useAppState'
import { setIsWorkspaceSelectorOpen } from '@/redux/store/slices/ui/uiSlice'
import type { Organization } from '@stytch/vanilla-js'
import { useDebounce } from '@uidotdev/usehooks'
import { Plus, Search } from 'lucide-react'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { DeleteConfirmationDialog } from '../DeleteConfirmationDialog'
import { WorkspaceSummaryCard } from '../WorkspaceSummaryCard'
import { WorkspaceSummaryCardSkeleton } from '../WorkspaceSummaryCardSkeleton'

import type { FC } from 'react'

export interface WorkspaceListProps {
	workspaces: Organization[]
	onAddNew: () => void
	onWorkspaceSelect?: (workspace?: Organization) => void
	isLoading?: boolean
}

export const WorkspaceList: FC<WorkspaceListProps> = ({
	workspaces: allWorkspaces,
	onAddNew,
	onWorkspaceSelect,
	isLoading,
}) => {
	const [filter, setFilter] = useState('')
	const [workspaceToDelete, setWorkspaceToDelete] = useState<Organization | null>(null)
	const { deleteWorkspace: deleteStytchWorkspace } = useStytchWorkspaces()
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const debouncedQuery = useDebounce(filter, 250)
	const workspaces = debouncedQuery
		? allWorkspaces.filter(({ organization_name }) =>
				organization_name.toLowerCase().includes(debouncedQuery.toLowerCase())
			)
		: allWorkspaces

	const handleDeleteWorkspace = async () => {
		if (!workspaceToDelete) return

		try {
			await deleteStytchWorkspace()
			dispatch(setIsWorkspaceSelectorOpen(false))
			enqueueSnackbar(`Workspace ${workspaceToDelete.organization_name} deleted`, {
				variant: 'success',
			})
			if (location.pathname.includes(workspaceToDelete.organization_slug)) {
				navigate('/dashboard')
			}
		} catch (error) {
			enqueueSnackbar(`Failed to delete workspace ${workspaceToDelete.organization_name}`, {
				variant: 'error',
			})
		} finally {
			setWorkspaceToDelete(null)
		}
	}

	return (
		<div className="p-6 flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<h2 className="text-2xl font-semibold">Workspaces</h2>
				<Button onClick={onAddNew} className="cursor-pointer">
					<Plus className="h-4 w-4 mr-2" />
					New Workspace
				</Button>
			</div>

			<div className="relative">
				<Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
				<Input
					placeholder="Search workspaces"
					value={filter}
					disabled={isLoading}
					onChange={(e) => setFilter(e.target.value)}
					className="pl-8"
				/>
			</div>

			<ScrollArea className="h-[70vh]">
				<div className="flex flex-col gap-2 p-1">
					{isLoading ? (
						<>
							<WorkspaceSummaryCardSkeleton />
							<WorkspaceSummaryCardSkeleton />
							<WorkspaceSummaryCardSkeleton />
						</>
					) : (
						workspaces
							.toSorted((a, b) => a.organization_name.localeCompare(b.organization_name))
							.map((workspace) => (
								<WorkspaceSummaryCard
									workspace={workspace}
									key={workspace.organization_slug}
									onWorkspaceSelect={() => onWorkspaceSelect?.(workspace)}
									onDelete={() => setWorkspaceToDelete(workspace)}
								/>
							))
					)}
				</div>
			</ScrollArea>

			<DeleteConfirmationDialog
				isOpen={!!workspaceToDelete}
				title={workspaceToDelete?.organization_name ?? ''}
				message={`Are you sure you want to delete the workspace ${workspaceToDelete?.organization_name}?`}
				onRemove={handleDeleteWorkspace}
				onCancel={() => setWorkspaceToDelete(null)}
			/>
		</div>
	)
}
